package com.yendo.mx.ui.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme // 🚀 DARK MODE
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage // 🚀 CARGA DE FOTO
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import androidx.compose.ui.platform.LocalUriHandler
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.Producto
import com.yendo.mx.repository.AuthRepository // 🚀 CONEXIÓN CON USUARIO EN VIVO
import com.yendo.mx.repository.ConfigGlobalRepository
import com.yendo.mx.ui.theme.RosaYendo
import kotlinx.coroutines.launch
import com.yendo.mx.utils.BackHandler

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PerfilScreen(
    onBack: () -> Unit,
    onCerrarSesion: () -> Unit,
    onRestauranteClick: (RestauranteFirebase) -> Unit = {},
    onProductoClick: (Producto, RestauranteFirebase) -> Unit = { _, _ -> }
) {

    BackHandler {
        onBack()
    }
    // 🚀 LÓGICA DE DARK MODE
    val isDark = isSystemInDarkTheme()
    val colorFondoGlobal = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    // --- ESTADOS PARA LA NAVEGACIÓN Y DIÁLOGOS ---
    var mostrarDialogoSalir by remember { mutableStateOf(false) }

    // Scope para ejecutar funciones
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current

    // 🚀 OBTENER CONFIG GLOBAL (Links, Soporte, Legales)
    var configGlobal by remember { mutableStateOf(com.yendo.mx.models.AppGlobalConfig()) }
    LaunchedEffect(Unit) {
        configGlobal = ConfigGlobalRepository.obtenerConfiguracionGlobal()
    }

    // 🚀 ESTADOS PARA LAS SUBPANTALLAS (ENRUTAMIENTO LOCAL)
    var mostrarHistorial by remember { mutableStateOf(false) }
    var mostrarEditarPerfil by remember { mutableStateOf(false) }
    var mostrarMisTarjetas by remember { mutableStateOf(false) } // NUEVO ESTADO PARA TARJETAS
    var mostrarDispositivos by remember { mutableStateOf(false) } // 🚀 NUEVO ESTADO
    var mostrarFavoritos by remember { mutableStateOf(false) } // 🚀 NUEVO ESTADO

    // 🚀 OBTENER USUARIO EN TIEMPO REAL
    val usuario by AuthRepository.usuarioActual.collectAsState()
    val nombre = usuario?.nombre ?: "Usuario"
    val fotoUrl = usuario?.fotoPerfil ?: ""

    // 🚀 EXTRAER INICIALES AUTOMÁTICAMENTE (Si se llama Luis Eduardo -> LE)
    val iniciales = remember(nombre) {
        nombre.split(" ")
            .mapNotNull { it.firstOrNull()?.uppercase() }
            .take(2)
            .joinToString("")
    }

    // --- ENRUTADOR INTERNO DEL PERFIL ---
    if (mostrarEditarPerfil) {
        EditarPerfilScreen(onBack = { mostrarEditarPerfil = false }, onCerrarSesion = onCerrarSesion)
    } else if (mostrarHistorial) {
        HistorialPedidosScreen(onBack = { mostrarHistorial = false })
    } else if (mostrarMisTarjetas) {
        // 🚀 ABRIMOS LA PANTALLA DE TARJETAS
        MisTarjetasScreen(onBack = { mostrarMisTarjetas = false })
    } else if (mostrarDispositivos) {
        // 🚀 ABRIMOS LA PANTALLA DE DISPOSITIVOS
        DispositivosScreen(onBack = { mostrarDispositivos = false })
    } else if (mostrarFavoritos) {
        // 🚀 ABRIMOS LA PANTALLA DE FAVORITOS
        MisFavoritosScreen(
            onBack = { mostrarFavoritos = false },
            onRestauranteClick = { rest ->
                mostrarFavoritos = false
                onBack() // Cerramos Perfil
                onRestauranteClick(rest)
            },
            onProductoClick = { prod, rest ->
                mostrarFavoritos = false
                onBack() // Cerramos Perfil
                onProductoClick(prod, rest)
            }
        )
    } else {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Mi Perfil", fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal) },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás", tint = colorTextoPrincipal)
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(containerColor = colorFondoGlobal)
                )
            },
            containerColor = colorFondoGlobal
        ) { padding ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .verticalScroll(rememberScrollState())
            ) {
                // --- SECCIÓN 1: CABECERA DEL USUARIO (ESTILO CLEAN) ---
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { mostrarEditarPerfil = true }
                        .padding(horizontal = 24.dp, vertical = 24.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // 🚀 FOTO DE PERFIL CIRCULAR
                    Box(modifier = Modifier.size(70.dp)) {
                        if (fotoUrl.isNotEmpty()) {
                            AsyncImage(
                                model = ImageRequest.Builder(LocalPlatformContext.current)
                                    .data(fotoUrl)
                                    .crossfade(true)
                                    .build(),
                                contentDescription = "Foto de perfil",
                                modifier = Modifier
                                    .fillMaxSize()
                                    .clip(CircleShape),
                                contentScale = ContentScale.Crop
                            )
                        } else {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .clip(CircleShape)
                                    .background(RosaYendo.copy(alpha = 0.1f)),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = iniciales.ifEmpty { "Y" },
                                    color = RosaYendo,
                                    fontWeight = FontWeight.Black,
                                    fontSize = 28.sp
                                )
                            }
                        }
                    }

                    Spacer(Modifier.width(20.dp))

                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = nombre,
                            fontWeight = FontWeight.Black,
                            fontSize = 22.sp,
                            color = colorTextoPrincipal
                        )
                        Text(
                            text = "Editar detalles personales",
                            color = colorTextoSecundario,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Medium
                        )
                    }

                    Icon(
                        Icons.Default.ChevronRight,
                        null,
                        tint = colorTextoSecundario,
                        modifier = Modifier.size(24.dp)
                    )
                }

                Spacer(Modifier.height(8.dp))

                // --- SECCIÓN: MI CUENTA ---
                CategoryLabel("Perfil", colorTextoSecundario)
                OpcionPerfilClean(
                    icono = Icons.Default.Person,
                    fondoIcono = Color(0xFFFF9800),
                    texto = "Editar Perfil",
                    colorTexto = colorTextoPrincipal,
                    onClick = { mostrarEditarPerfil = true }
                )
                OpcionPerfilClean(
                    icono = Icons.Default.List,
                    fondoIcono = Color(0xFF2196F3),
                    texto = "Historial de pedidos",
                    colorTexto = colorTextoPrincipal,
                    onClick = { mostrarHistorial = true }
                )
                OpcionPerfilClean(
                    icono = Icons.Default.CreditCard,
                    fondoIcono = Color(0xFF4CAF50),
                    texto = "Mis Tarjetas",
                    colorTexto = colorTextoPrincipal,
                    onClick = { mostrarMisTarjetas = true }
                )
                OpcionPerfilClean(
                    icono = Icons.Default.Favorite,
                    fondoIcono = Color(0xFFE91E63),
                    texto = "Mis Favoritos",
                    colorTexto = colorTextoPrincipal,
                    onClick = { mostrarFavoritos = true }
                )
                OpcionPerfilClean(
                    icono = Icons.Default.PhonelinkSetup,
                    fondoIcono = Color(0xFF673AB7),
                    texto = "Dispositivos",
                    colorTexto = colorTextoPrincipal,
                    onClick = { mostrarDispositivos = true }
                )

                Spacer(Modifier.height(16.dp))

                // --- SECCIÓN: AYUDA ---
                CategoryLabel("Ayuda y Soporte", colorTextoSecundario)
                OpcionPerfilClean(
                    icono = Icons.Default.SupportAgent,
                    fondoIcono = Color(0xFF9C27B0),
                    texto = "Ayuda con un pedido",
                    colorTexto = colorTextoPrincipal,
                    onClick = {
                        try {
                            val urlAyudaPedido = configGlobal.url_ayuda_pedido.ifBlank { "https://www.yendo.com.mx/ayuda" }
                            uriHandler.openUri(urlAyudaPedido)
                        } catch (e: Exception) {}
                    }
                )
                OpcionPerfilClean(
                    icono = Icons.Default.Help,
                    fondoIcono = Color(0xFFFF5722),
                    texto = "Centro de Ayuda",
                    colorTexto = colorTextoPrincipal,
                    onClick = {
                        try {
                            val urlAyuda = configGlobal.correo_soporte.ifBlank { "https://www.yendo.com.mx/ayuda" }
                            uriHandler.openUri(urlAyuda)
                        } catch (e: Exception) {}
                    }
                )

                Spacer(Modifier.height(16.dp))

                // --- SECCIÓN: LEGAL ---
                CategoryLabel("Legal", colorTextoSecundario)
                OpcionPerfilClean(
                    icono = Icons.Default.Description,
                    fondoIcono = Color(0xFF607D8B),
                    texto = "Términos y Condiciones",
                    colorTexto = colorTextoPrincipal,
                    onClick = {
                        try {
                            val urlTerminos = configGlobal.url_terminos.ifBlank { "https://www.yendo.com.mx/terminos-y-condiciones" }
                            uriHandler.openUri(urlTerminos)
                        } catch (e: Exception) {}
                    }
                )
                OpcionPerfilClean(
                    icono = Icons.Default.PrivacyTip,
                    fondoIcono = Color(0xFFE91E63),
                    texto = "Aviso de Privacidad",
                    colorTexto = colorTextoPrincipal,
                    onClick = {
                        try {
                            val urlPrivacidad = configGlobal.url_privacidad.ifBlank { "https://www.yendo.com.mx/privacidad" }
                            uriHandler.openUri(urlPrivacidad)
                        } catch (e: Exception) {}
                    }
                )

                Spacer(Modifier.height(16.dp))

                // --- SECCIÓN: SESIÓN ---
                CategoryLabel("Sesión", colorTextoSecundario)
                OpcionPerfilClean(
                    icono = Icons.AutoMirrored.Filled.ExitToApp,
                    fondoIcono = Color(0xFFFF5252),
                    texto = "Cerrar Sesión",
                    colorTexto = colorTextoPrincipal,
                    onClick = { mostrarDialogoSalir = true }
                )

                Spacer(Modifier.height(40.dp))

                Text(
                    text = "Yendo Mx v1.0.0",
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Center,
                    color = colorTextoSecundario.copy(alpha = 0.5f),
                    fontSize = 12.sp
                )
                Spacer(Modifier.height(20.dp))
            }

            // --- DIÁLOGOS DE CONFIRMACIÓN ---

            if (mostrarDialogoSalir) {
                AlertDialog(
                    containerColor = if (isDark) Color(0xFF1E1E1E) else Color.White,
                    onDismissRequest = { mostrarDialogoSalir = false },
                    title = { Text("Cerrar Sesión", fontWeight = FontWeight.Bold, color = colorTextoPrincipal) },
                    text = { Text("¿Estás seguro de que deseas salir de tu cuenta?", color = colorTextoSecundario) },
                    confirmButton = {
                        Button(
                            onClick = {
                                // LOGICA DE CERRAR SESIÓN CON FIREBASE
                                scope.launch {
                                    AuthRepository.cerrarSesion()
                                    mostrarDialogoSalir = false
                                    onCerrarSesion() // Le avisamos a HomeScreen/App que regrese al Login
                                }
                            },
                            colors = ButtonDefaults.buttonColors(RosaYendo)
                        ) { Text("Cerrar Sesión", color = Color.White) }
                    },
                    dismissButton = {
                        TextButton(onClick = { mostrarDialogoSalir = false }) { Text("Cancelar", color = colorTextoSecundario) }
                    }
                )
            }
        }
    }
}

// --- COMPONENTES CLEAN ---

@Composable
fun CategoryLabel(text: String, color: Color) {
    Text(
        text = text,
        modifier = Modifier.padding(start = 24.dp, end = 24.dp, top = 24.dp, bottom = 12.dp),
        color = color,
        fontSize = 14.sp,
        fontWeight = FontWeight.Bold
    )
}

@Composable
fun OpcionPerfilClean(
    icono: ImageVector,
    fondoIcono: Color,
    texto: String,
    colorTexto: Color,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .padding(horizontal = 24.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Icono con fondo circular colorido
        Box(
            modifier = Modifier
                .size(40.dp)
                .clip(CircleShape)
                .background(fondoIcono),
            contentAlignment = Alignment.Center
        ) {
            Icon(icono, contentDescription = null, tint = Color.White, modifier = Modifier.size(20.dp))
        }
        
        Spacer(Modifier.width(16.dp))
        
        Text(
            text = texto,
            fontSize = 16.sp,
            color = colorTexto,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.weight(1f)
        )
        
        Icon(
            Icons.Default.ChevronRight,
            contentDescription = null,
            tint = Color.LightGray.copy(alpha = 0.6f),
            modifier = Modifier.size(20.dp)
        )
    }
}
